"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RETENTION_QUERY_KEYS = exports.RETENTION_PATH = exports.retentionOverviewQuerySchema = exports.RETENTION_DUE_PAGE_SIZE_MAX = exports.RETENTION_DUE_PAGE_SIZE = exports.RETENTION_DUE_SORTS = exports.RETENTION_DUE_STATUS_ORDER = exports.RETENTION_DUE_STATUS_LABELS = exports.releaseCandidateSchema = exports.keepCandidateSchema = exports.retentionSettingsSchema = exports.RETENTION_REASON_LABELS = exports.RETENTION_CLOSING_STAGES = exports.DEFAULT_RETENTION_SETTINGS = exports.RETENTION_MODES = exports.ANONYMISED_NAME = exports.REMOVED_TEXT = exports.LIVE_CONFIRMATION_WORD = exports.RECOMMENDED_DRY_RUNS = exports.ARCHIVE_GRACE_DAYS = exports.RETENTION_MAX_DAYS = exports.RETENTION_MIN_DAYS = exports.RETENTION_DEFAULT_DAYS = void 0;
exports.retentionStateOf = retentionStateOf;
exports.isDueByMonthEnd = isDueByMonthEnd;
exports.graceEndsAt = graceEndsAt;
exports.retentionDueStatus = retentionDueStatus;
const zod_1 = require("zod");
/**
 * S30 — D15 retention: 90 days after an application reaches a terminal status, unless a talent
 * pool or another live application holds the person, or the HR Lead keeps them permanently. On
 * expiry the record is anonymised, never deleted (#6), after a monthly archive and 7 days' grace.
 */
exports.RETENTION_DEFAULT_DAYS = 90;
exports.RETENTION_MIN_DAYS = 30;
exports.RETENTION_MAX_DAYS = 3650;
/** D15: nothing is anonymised until 7 days after the archive went out. */
exports.ARCHIVE_GRACE_DAYS = 7;
/** The plan: run dry for the first two months before enabling live deletion. */
exports.RECOMMENDED_DRY_RUNS = 2;
/** Q16: what the HR Lead types to switch to live anonymisation. */
exports.LIVE_CONFIRMATION_WORD = 'ANONYMISE';
/** What every removed text reads (Q14). */
exports.REMOVED_TEXT = 'Removed under retention';
exports.ANONYMISED_NAME = 'Anonymised candidate';
exports.RETENTION_MODES = ['dry_run', 'live'];
exports.DEFAULT_RETENTION_SETTINGS = { periodDays: exports.RETENTION_DEFAULT_DAYS, mode: 'dry_run' };
/**
 * Q13: these close an application for retention. Auto-rejected counts like rejected; a transfer
 * never does (its new application is live); `joined` is staff and is never anonymised.
 */
exports.RETENTION_CLOSING_STAGES = ['rejected', 'auto_rejected', 'withdrawn', 'closed_job'];
exports.RETENTION_REASON_LABELS = {
    application_closed: 'Last application closed',
    left_pool: 'Left the talent pool',
    record_made: 'Record made, never applied or pooled',
};
const DAY_MS = 86_400_000;
/**
 * The one rule (D15, S30 build notes, DECISIONS-LOG S29–S32 Q11–Q13): kept overrides everything;
 * a joined person is staff; a live application or a live pool membership suspends; otherwise the
 * countdown runs from the latest of the last closed application and the last pool membership
 * ending — or, with neither, from when the record was made (Q12).
 */
function retentionStateOf(facts, periodDays, now) {
    if (facts.isAnonymised)
        return { kind: 'anonymised' };
    if (facts.isKept)
        return { kind: 'kept' };
    if (facts.applications.some((application) => application.stage === 'joined'))
        return { kind: 'staff' };
    const live = facts.applications.filter((application) => application.stage !== 'transferred' && !exports.RETENTION_CLOSING_STAGES.includes(application.stage) && application.closedAt === null);
    if (live.length > 0)
        return { kind: 'in_progress', liveApplications: live.length };
    const livePools = facts.memberships.filter((membership) => !membership.isRemoved && membership.endsAt.getTime() > now.getTime());
    if (livePools.length > 0)
        return { kind: 'pooled', until: new Date(Math.max(...livePools.map((membership) => membership.endsAt.getTime()))) };
    const closed = facts.applications.flatMap((application) => (application.closedAt ? [application.closedAt.getTime()] : []));
    const left = facts.memberships.map((membership) => membership.endsAt.getTime());
    const lastClosed = closed.length > 0 ? Math.max(...closed) : null;
    const lastLeft = left.length > 0 ? Math.max(...left) : null;
    let from = facts.createdAt.getTime();
    let reason = 'record_made';
    if (lastClosed !== null && (lastLeft === null || lastClosed >= lastLeft)) {
        from = lastClosed;
        reason = 'application_closed';
    }
    else if (lastLeft !== null) {
        from = lastLeft;
        reason = 'left_pool';
    }
    return { kind: 'counting', from: new Date(from), reason, dueAt: new Date(from + periodDays * DAY_MS) };
}
/** The archive for the month starting `monthStart` covers everyone due before the month ends. */
function isDueByMonthEnd(dueAt, monthEnd) {
    return dueAt.getTime() < monthEnd.getTime();
}
/** D15: anonymise only after the archive that listed them went out and its grace has passed. */
function graceEndsAt(sentAt) {
    return new Date(sentAt.getTime() + exports.ARCHIVE_GRACE_DAYS * DAY_MS);
}
// --- Inputs ------------------------------------------------------------------------------------
exports.retentionSettingsSchema = zod_1.z.object({
    periodDays: zod_1.z.number().int().min(exports.RETENTION_MIN_DAYS, `At least ${exports.RETENTION_MIN_DAYS} days.`).max(exports.RETENTION_MAX_DAYS, `At most ${exports.RETENTION_MAX_DAYS} days.`),
    mode: zod_1.z.enum(exports.RETENTION_MODES),
    /** Q16: switching to live needs the word typed. */
    confirmation: zod_1.z.string().trim().optional(),
    version: zod_1.z.number().int().min(0),
});
exports.keepCandidateSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().min(3, 'Say why they are kept.').max(500),
    version: zod_1.z.number().int().min(1),
});
exports.releaseCandidateSchema = zod_1.z.object({ version: zod_1.z.number().int().min(1) });
exports.RETENTION_DUE_STATUS_LABELS = {
    would_be_anonymised: 'Would be anonymised',
    past_due: 'Past due',
    waiting_for_archive: 'Waiting for the archive',
    anonymised_tonight: 'Anonymised tonight',
};
/** Least to most pressing — the order "Status" sorts in. */
exports.RETENTION_DUE_STATUS_ORDER = ['would_be_anonymised', 'past_due', 'waiting_for_archive', 'anonymised_tonight'];
function retentionDueStatus(row, mode) {
    if (mode === 'live')
        return row.isReady ? 'anonymised_tonight' : 'waiting_for_archive';
    return row.isPastDue ? 'past_due' : 'would_be_anonymised';
}
/** The due table is paged on the server (it can hold everyone past due while in dry run). */
exports.RETENTION_DUE_SORTS = ['candidate', 'reason', 'from', 'due', 'files', 'status'];
exports.RETENTION_DUE_PAGE_SIZE = 50;
exports.RETENTION_DUE_PAGE_SIZE_MAX = 200;
exports.retentionOverviewQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(exports.RETENTION_DUE_PAGE_SIZE_MAX).default(exports.RETENTION_DUE_PAGE_SIZE),
    sort: zod_1.z.enum(exports.RETENTION_DUE_SORTS).default('due'),
    dir: zod_1.z.enum(['asc', 'desc']).default('asc'),
});
exports.RETENTION_PATH = '/admin/retention';
exports.RETENTION_QUERY_KEYS = [['retention']];
//# sourceMappingURL=retention.js.map